import { Fragment, useEffect, useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { Col, Modal, Row } from "react-bootstrap";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { Button } from "../../common/Button";
import { FormikObjInboundCall } from "../../Admin/Dashboard/Constant";
import SearchSelect from "../../common/textfield/SearchSelect";
import TextInput from "../../common/textfield/TextInput";
import Fields from "../../Admin/Dashboard/Fields";
import { getServiceLineById, getServiceLinesByOrgId } from "../../api/ServiceLine";
import { getQuestionnaireById } from "../../api/Questionnaire";
import { getEncounterRecord } from "../api/AppointmentRequests";
import { failed, success } from "../../common/Toastify";
import { evaluateCondition } from "../../../utils/questionnaireSupport";
import { applyIntakeQuestionsToQuestionResponse } from "./internalReferralPrefill";
import AddToWaitingRoomModal from "../../Admin/Dashboard/AddToWaitingRoomModal";
import Appointment from "../Appointments/Appointment";
import { createInternalReferral } from "../api/ServiceRequest";
import {
    buildInternalReferralPayload,
    buildWaitingRoomPayload,
    buildAppointmentPayload,
    buildFhirReference,
    buildQuestionnairePayload,
    mapToReferralType,
} from "./internalReferralPayload";

const steps = ["Organization & Service Line", "Referral Details"];

const InternalReferralWizard = ({
    modalShow,
    onClose,
    onSuccess,
    sourceEncounterId,
    patientId,
    referralType,
}) => {
    const user = useSelector((state) => state?.auth?.user);
    const isScheduleReferral = referralType === "SCHEDULE";
    const [activeStep, setActiveStep] = useState(0);
    const [wrModalShow, setWrModalShow] = useState(false);
    const [appointmentModalShow, setAppointmentModalShow] = useState(isScheduleReferral);
    const [sourceEncounter, setSourceEncounter] = useState(null);
    const [availableServiceLines, setAvailableServiceLines] = useState([]);
    const [encounterLoading, setEncounterLoading] = useState(false);
    const [Questions, setQuestions] = useState([]);
    const [questionnaireDefinition, setQuestionnaireDefinition] = useState(null);
    const [visibleQuestions, setVisibleQuestions] = useState([]);
    const [questionResponseKey, setQuestionResponseKey] = useState(0);
    const [answersPrefilledFromEncounter, setAnswersPrefilledFromEncounter] = useState(false);

    const formik = useFormik({
        ...FormikObjInboundCall,
        initialValues: {
            ...FormikObjInboundCall.initialValues,
            reasonForReferral: "",
            patientId: patientId || "",
            encounterId: sourceEncounterId || "",
        },
        onSubmit: () => {},
    });

    useEffect(() => {
        if (!modalShow || !sourceEncounterId) return;

        setEncounterLoading(true);
        getEncounterRecord({ encounterId: sourceEncounterId })
            .then((res) => {
                if (res?.status !== true) return;
                const data = res?.data;
                setSourceEncounter(data);

                if (data?.intakeQuestions?.intakeReason?.trim()) {
                    formik.setFieldValue(
                        "reasonForReferral",
                        data.intakeQuestions.intakeReason.trim()
                    );
                }

                const orgId =
                    data?.serviceProvider?.reference?.split("/")?.[1] ||
                    data?.managingOrganization?.reference?.split("/")?.[1] ||
                    data?.orgId ||
                    "";
                const orgName =
                    data?.serviceProvider?.display ||
                    data?.orgName ||
                    "";

                formik.setFieldValue("orgId", orgId);
                formik.setFieldValue("orgName", orgName);

                if (data?.serviceLineId) {
                    formik.setFieldValue("serviceLineId", data.serviceLineId);
                    if (isScheduleReferral && orgId) {
                        getServiceLineById(orgId, data.serviceLineId)
                            .then((slRes) => {
                                if (slRes?.data?.name) {
                                    formik.setFieldValue("serviceLineName", {
                                        name: slRes.data.name,
                                        id: data.serviceLineId,
                                    });
                                }
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch((err) => {
                failed(
                    err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        err.message
                );
                if (isScheduleReferral) {
                    onClose?.();
                }
            })
            .finally(() => setEncounterLoading(false));
    }, [modalShow, sourceEncounterId, isScheduleReferral]);

    useEffect(() => {
        if (isScheduleReferral || !formik.values.orgId) return;

        getServiceLinesByOrgId(formik.values.orgId)
            .then((res) => {
                if (res?.status !== true) return;

                const formatted = res.data
                    .map((item) => ({
                        name: item.name,
                        value: item.id,
                        id: item.id,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                setAvailableServiceLines(formatted);

                const defaultServiceLineId =
                    sourceEncounter?.serviceLineId ||
                    formik.values.serviceLineId;

                if (defaultServiceLineId) {
                    const match = formatted.find((sl) => sl.id === defaultServiceLineId);
                    if (match) {
                        formik.setFieldValue("serviceLineId", match.id);
                        formik.setFieldValue("serviceLineName", match);
                    }
                } else if (formatted.length === 1) {
                    formik.setFieldValue("serviceLineId", formatted[0].id);
                    formik.setFieldValue("serviceLineName", formatted[0]);
                }
            })
            .catch((err) => {
                failed(
                    err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        err.message ||
                        "Error loading service lines"
                );
            });
    }, [formik.values.orgId, sourceEncounter?.serviceLineId, isScheduleReferral]);

    useEffect(() => {
        if (isScheduleReferral || !formik.values.serviceLineId || !formik.values.orgId) return;

        getServiceLineById(formik.values.orgId, formik.values.serviceLineId)
            .then(async (res) => {
                if (!res?.data?.intakeQuestionnaire) {
                    failed("No intake questionnaire found for this service line");
                    setQuestions([]);
                    formik.setFieldValue("questionResponse", []);
                    return;
                }

                const response = await getQuestionnaireById(
                    res.data.intakeQuestionnaire,
                    res.data.orgId
                );
                formik.setFieldValue("questionnaireId", response?.data?.id);
                setQuestionnaireDefinition(response?.data);

                const questions = response?.data?.item?.map((item) => ({
                    ...item,
                    name: item?.text,
                    value: item?.id,
                }));
                setQuestions(questions || []);

                const questionResponse = questions?.map((item) => {
                    const isTextType =
                        item?.answerType === "freeText" ||
                        item?.answerType === "singleFreeText" ||
                        item?.answerType === "dropDown";
                    const isDropDown = item?.answerType === "dropDown";
                    return {
                        ...item,
                        questionId: item?.id || item?.linkId || null,
                        tag: item?.tag,
                        questionType: item?.answerType,
                        answerType: item?.answerType,
                        answer: item?.answerOption?.map((option) => ({
                            id: option?.id,
                            required: item?.required,
                            answer: isTextType ? "" : false,
                            option: isTextType ? (isDropDown ? option.text : null) : option?.text,
                            value: isDropDown ? option.text : null,
                            name: isDropDown ? option.text : null,
                        })),
                    };
                });

                let mergedResponse = questionResponse || [];
                const intake = sourceEncounter?.intakeQuestions;

                if (intake && Object.keys(intake).length) {
                    const { questionResponse: merged, didPrefill } =
                        applyIntakeQuestionsToQuestionResponse(questionResponse, intake, {
                            patientLocation: sourceEncounter?.patientLocation,
                        });
                    mergedResponse = merged;
                    setAnswersPrefilledFromEncounter(didPrefill);
                } else {
                    setAnswersPrefilledFromEncounter(false);
                }

                formik.setFieldValue("questionResponse", mergedResponse);
            })
            .catch((err) => {
                failed(
                    err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        err.message ||
                        "Error loading intake questionnaire"
                );
            });
    }, [
        formik.values.serviceLineId,
        formik.values.orgId,
        sourceEncounter?.intakeQuestions,
        sourceEncounter?.patientLocation,
        isScheduleReferral,
    ]);

    useEffect(() => {
        if (isScheduleReferral) return;
        if (!questionnaireDefinition || !formik.values?.questionResponse || Questions.length === 0) {
            if (Questions.length > 0) {
                setVisibleQuestions(Questions.map(() => true));
            }
            return;
        }

        const visibility = Questions.map((question) => {
            let questionnaireItem =
                questionnaireDefinition?.item?.find(
                    (q) => q.id === question.id && q.linkId === question.linkId
                ) ||
                questionnaireDefinition?.item?.find((q) => q.linkId === question.linkId) ||
                questionnaireDefinition?.item?.find((q) => q.id === question.id);

            if (!questionnaireItem?.enableWhen) return true;

            const questionResponse = formik.values.questionResponse.map((item, index) => ({
                ...item,
                questionId: item.questionId || item.id || Questions[index]?.id || Questions[index]?.linkId || null,
                tag: item?.tag,
                questionType: item.questionType || item.answerType,
                answerType: item.answerType || item.questionType,
            }));

            return evaluateCondition(questionnaireItem.enableWhen.expression, questionResponse);
        });

        setVisibleQuestions(visibility);
    }, [questionnaireDefinition, Questions, questionResponseKey, formik.values.questionResponse]);

    useEffect(() => {
        if (isScheduleReferral) return;
        if (!formik.values?.questionResponse || Questions.length === 0) return;

        const timer = setTimeout(() => {
            setQuestionResponseKey((prev) => prev + 1);
        }, 50);

        return () => clearTimeout(timer);
    }, [
        formik.values.questionResponse
            ?.map((q) => {
                if (q.answerType === "radio" || q.answerType === "checkbox") {
                    return q.answer?.filter((a) => a.answer === true).map((a) => a.option).join(",");
                }
                return q.answer?.[0]?.answer || "";
            })
            .join("|"),
        Questions.length,
    ]);

    const orgSelectOptions = formik.values.orgId
        ? [{ label: formik.values.orgName, value: formik.values.orgId }]
        : [];

    const serviceLineSelectOptions = availableServiceLines.map((sl) => ({
        label: sl.name,
        value: sl.id,
    }));

    const handleServiceLineChange = (selected) => {
        const selectedSL = availableServiceLines.find((item) => item.id === selected?.value);
        formik.setFieldValue("serviceLineId", selected?.value || "");
        formik.setFieldValue("serviceLineName", selectedSL || "");
    };

    const getServiceLineName = () =>
        formik.values.serviceLineName?.name || formik.values.serviceLineName || "";

    const getReferralAuthor = () => ({
        authorId: user?.["custom:practitioner_id"],
        authorType: "Practitioner",
        authorName: user?.name?.[0]?.text?.trim() || "",
    });

    const getPatientReference = () =>
        buildFhirReference(
            "Patient",
            patientId,
            sourceEncounter?.subject?.display ||
                sourceEncounter?.patient?.display ||
                ""
        );

    const getOrganizationReference = () =>
        buildFhirReference(
            "Organization",
            formik.values.orgId,
            formik.values.orgName
        );

    const getServiceLineReference = () =>
        buildFhirReference(
            "ServiceLine",
            formik.values.serviceLineId,
            getServiceLineName()
        );

    const submitInternalReferral = (branchPayload, overrides = {}) => {
        const reasonForReferral =
            overrides.reasonForReferral ?? formik.values.reasonForReferral;
        const questionnaireId =
            overrides.questionnaireId !== undefined
                ? overrides.questionnaireId
                : formik.values.questionnaireId;
        const questionResponse =
            overrides.questionResponse !== undefined
                ? overrides.questionResponse
                : formik.values.questionResponse;

        const questionnaire =
            overrides.questionnaire === null
                ? null
                : buildQuestionnairePayload({
                      questionnaireId,
                      questionResponse,
                      author: getReferralAuthor(),
                  });

        const payload = buildInternalReferralPayload({
            sourceEncounterId,
            patient: getPatientReference(),
            organization: getOrganizationReference(),
            serviceLine: getServiceLineReference(),
            proceedWith: referralType,
            referralType: mapToReferralType(referralType),
            reasonForReferral,
            patientLocation: sourceEncounter?.patientLocation,
            questionnaire,
            ...branchPayload,
        });

        return createInternalReferral(payload)
            .then((res) => {
                success("Internal referral submitted successfully");
                setWrModalShow(false);
                setAppointmentModalShow(false);
                onSuccess?.();
                handleClose();
                return res;
            })
            .catch((err) => {
                failed(
                    err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        err.message ||
                        "Failed to submit internal referral"
                );
            });
    };

    const handleWaitingRoomSubmit = (wrValues) =>
        submitInternalReferral({
            waitingRoom: buildWaitingRoomPayload(wrValues, {
                questionResponse: formik.values.questionResponse,
                reasonForReferral: formik.values.reasonForReferral,
                patientDisplay: getPatientReference().display,
            }),
        });

    const handleAppointmentSubmit = (apptValues) =>
        submitInternalReferral(
            {
                appointment: buildAppointmentPayload(apptValues),
            },
            isScheduleReferral
                ? {
                      reasonForReferral: apptValues.title,
                      questionnaire: null,
                  }
                : {}
        );

    const handleAppointmentClose = () => {
        setAppointmentModalShow(false);
        if (isScheduleReferral) {
            handleClose();
        }
    };

    const getWrModalData = () => ({
        orgId: formik.values.orgId,
        encounterId: sourceEncounterId,
        patient: sourceEncounter?.patient || {
            reference: `Patient/${patientId}`,
            type: "Patient",
            display: sourceEncounter?.subject?.display || "",
        },
        patientLocation: sourceEncounter?.patientLocation,
    });

    const isSelfCallingNo = () => {
        const selfCalling = formik.values.questionResponse?.find((q) => q.tag === "SELFCALLING");
        return selfCalling?.answer?.some((opt) => opt.answer === true && opt.option === "No");
    };

    const validateStep = (step) => {
        const errors = {};
        if (step === 0) {
            if (!formik.values.orgId) errors.orgId = "Required field";
            if (!formik.values.serviceLineId) errors.serviceLineId = "Required field";
        }
        if (step === 1) {
            if (!formik.values.reasonForReferral?.trim()) {
                errors.reasonForReferral = "Required field";
            }
            if (Questions.length === 0) {
                errors.questionnaire = "Assign the questionnaire to proceed.";
            }

            const telePhoneField = formik.values.questionResponse?.find(
                (item) => item?.tag === "TELEPHONE"
            )?.answer?.[0]?.answer;
            const phonePattern = /^(?:\+?1[-. ]?)?\(?[0-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/;

            formik.values.questionResponse?.forEach((question, index) => {
                if (!visibleQuestions[index] || !question?.required) return;

                if (question.tag === "RELATIONSHIP" && question.answerType === "dropDown") {
                    if (!isSelfCallingNo()) return;
                    if (!question.answer?.some((opt) => opt.answer?.toString().trim().length > 0)) {
                        errors[`questionResponse[${index}]`] = "Required field";
                    }
                    return;
                }

                const answerType = question.answerType || question.questionType;

                if (answerType === "radio" || answerType === "checkbox") {
                    if (!question.answer?.some((item) => !!item?.answer)) {
                        errors[`questionResponse[${index}]`] = "Required field";
                    }
                    return;
                }

                if (
                    answerType === "freeText" ||
                    answerType === "singleFreeText" ||
                    answerType === "dropDown"
                ) {
                    if (question.tag === "CALLER" && answersPrefilledFromEncounter) {
                        if (!question.answer?.[0]?.answer?.trim()) {
                            errors[`questionResponse[${index}]`] = "Required field";
                        }
                        return;
                    }

                    if (
                        answerType === "dropDown" &&
                        !question.answer?.some((opt) => opt.answer?.toString().trim().length > 0)
                    ) {
                        errors[`questionResponse[${index}]`] = "Required field";
                        return;
                    }

                    if (
                        (answerType === "freeText" || answerType === "singleFreeText") &&
                        !question.answer?.[0]?.answer?.trim()
                    ) {
                        errors[`questionResponse[${index}]`] = "Required field";
                    }
                }
            });

            if (telePhoneField && !phonePattern.test(telePhoneField)) {
                const phoneIndex = formik.values.questionResponse?.findIndex(
                    (item) => item?.tag === "TELEPHONE"
                );
                if (phoneIndex >= 0 && visibleQuestions[phoneIndex]) {
                    errors[`questionResponse[${phoneIndex}]`] = "Enter Valid Phone Number.";
                }
            }
        }
        return errors;
    };

    const handleNext = async () => {
        const errors = validateStep(activeStep);
        if (Object.keys(errors).length > 0) {
            Object.entries(errors).forEach(([field, message]) => {
                formik.setFieldError(field, message);
            });
            failed("Please complete all required fields before proceeding.");
            return;
        }

        if (activeStep === steps.length - 1) {
            setWrModalShow(true);
            return;
        }

        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleClose = () => {
        setActiveStep(0);
        setSourceEncounter(null);
        setAvailableServiceLines([]);
        setQuestions([]);
        setQuestionnaireDefinition(null);
        setVisibleQuestions([]);
        setAnswersPrefilledFromEncounter(false);
        setWrModalShow(false);
        setAppointmentModalShow(false);
        formik.resetForm();
        onClose();
    };

    const referralTypeLabel =
        referralType === "WAITING_ROOM"
            ? "Add to Waiting Room"
            : "Book An Appointment";

    const activeForm = () => {
        switch (activeStep) {
            case 0:
                return (
                    <div>
                        {encounterLoading ? (
                            <p className="text-muted mb-0">Loading encounter…</p>
                        ) : (
                            <>
                                <Row>
                                    <Col>
                                        <SearchSelect
                                            label="Organization"
                                            required
                                            options={orgSelectOptions}
                                            value={
                                                orgSelectOptions.find(
                                                    (opt) => opt.value === formik.values.orgId
                                                ) || null
                                            }
                                            onChange={() => {}}
                                            placeholder="Organization"
                                            isDisabled
                                            formik={formik}
                                            keyField="orgId"
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <SearchSelect
                                            label="Service Line"
                                            required
                                            options={serviceLineSelectOptions}
                                            value={
                                                serviceLineSelectOptions.find(
                                                    (opt) => opt.value === formik.values.serviceLineId
                                                ) || null
                                            }
                                            onChange={handleServiceLineChange}
                                            placeholder="Select Service Line"
                                            formik={formik}
                                            keyField="serviceLineId"
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>
                );
            case 1:
                return (
                    <>
                        <Row>
                            <Col>
                                <TextInput
                                    keyField="reasonForReferral"
                                    label="Reason for Referral"
                                    placeholder="Reason for Referral"
                                    formik={formik}
                                    fieldtype="TextInput"
                                    required
                                />
                            </Col>
                        </Row>
                        {answersPrefilledFromEncounter ? (
                            <p className="text-muted mb-3">
                                Answers copied from the current encounter. You can edit them before proceeding.
                            </p>
                        ) : null}
                        {Questions?.length > 0 ? (
                            Questions.map((question, index) => {
                                if (!visibleQuestions[index]) return null;
                                return (
                                    <Fragment key={`${question.id}-${index}`}>
                                        <Fields
                                            question={question}
                                            Questions={Questions}
                                            formik={formik}
                                            propIndex={index}
                                            callerReadOnly={answersPrefilledFromEncounter}
                                            setRadioValue={() => {
                                                setTimeout(() => setQuestionResponseKey((prev) => prev + 1), 0);
                                            }}
                                        />
                                    </Fragment>
                                );
                            })
                        ) : (
                            <div className="error-text">
                                Assign the questionnaire to proceed.
                            </div>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    if (!modalShow) return null;

    if (isScheduleReferral) {
        if (encounterLoading || !formik.values.orgId) return null;

        return (
            <Appointment
                modalShow={appointmentModalShow}
                handleShow={appointmentModalShow}
                type="intake"
                patient={patientId}
                selectedOrgId={formik.values.orgId}
                isInternalReferral
                handleHide={handleAppointmentClose}
                onSubmitOverride={handleAppointmentSubmit}
            />
        );
    }

    return (
        <>
        {wrModalShow && (
            <AddToWaitingRoomModal
                data={getWrModalData()}
                onShow={wrModalShow}
                onHide={() => setWrModalShow(false)}
                handleShow={() => {}}
                onSubmitOverride={handleWaitingRoomSubmit}
            />
        )}
        <Modal
            show={modalShow}
            onHide={handleClose}
            size="xl"
            centered
            backdrop="static"
            dialogClassName="modal-xl-custom"
        >
            <Modal.Header closeButton>
                <Modal.Title>Internal Referral — {referralTypeLabel}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Box sx={{ width: "100%" }}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <form
                        className="common-form border-fields"
                        style={{
                            border: "1px solid #c7c7c7",
                            padding: "10px",
                            borderRadius: "5px",
                            marginTop: "10px",
                        }}
                        onSubmit={(e) => e.preventDefault()}
                    >
                        {activeForm()}
                    </form>
                    <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                        {activeStep !== 0 && (
                            <Button
                                variant="secondary"
                                title="Previous"
                                disabled={encounterLoading}
                                onClick={handleBack}
                            >
                                Previous
                            </Button>
                        )}
                        <Box sx={{ flex: "1 1 auto" }} />
                        <Button
                            title={activeStep === steps.length - 1 ? "Proceed" : "Next"}
                            disabled={encounterLoading}
                            onClick={handleNext}
                        >
                            {activeStep === steps.length - 1 ? "Proceed" : "Next"}
                        </Button>
                    </Box>
                </Box>
            </Modal.Body>
        </Modal>
        </>
    );
};

export default InternalReferralWizard;
